import React, {useEffect, useState} from 'react';
import {AutoComplete, Button, Input, Modal, Typography, Avatar, theme} from 'antd';
import {UserAddOutlined, TeamOutlined} from '@ant-design/icons';
import {UserDatas} from "../generated/user_pb";
import {tryParseUserDataFromLocalStorage} from "../utils/userStorage";
import {text} from '../utils/language';
import {EmptyState, PageSection, Surface} from '@dowty/ui';

const {Text} = Typography;

interface FriendListCardProps {
    friends: string[];
    onAddFriend: (username: string) => Promise<boolean>;
}

export const FriendListCard: React.FC<FriendListCardProps> = ({friends, onAddFriend}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedUsername, setSelectedUsername] = useState<string>('');
    const [adding, setAdding] = useState(false);
    const [options, setOptions] = useState<{ value: string }[]>([]);
    const [allUsers, setAllUsers] = useState<string[]>([]);
    const { token } = theme.useToken();

    useEffect(() => {
        let userString = localStorage.getItem('allUsers');
        if (userString == null) return;
        let userD = UserDatas.deserializeBinary(Uint8Array.from(atob(userString), c => c.codePointAt(0)!));
        let userFriends = localStorage.getItem('friends');
        let userF: UserDatas;
        if (userFriends !== null && userFriends.trim().length > 0) {
            userF = UserDatas.deserializeBinary(Uint8Array.from(atob(userFriends), c => c.codePointAt(0)!));
        } else {
            userF = new UserDatas();
        }
        const actCurUser = tryParseUserDataFromLocalStorage(true);
        const result: string[] = [];

        for (const u of userD.getUserList()) {
            if (actCurUser?.getUserid() === u.getUserid()) continue;
            const isFriend = userF.getUserList().some(f => f.getUserid() === u.getUserid());
            if (!isFriend) result.push(u.getUsername());
        }
        setAllUsers(result);
    }, [friends]);

    const handleOk = async () => {
        setAdding(true);
        try {
            if (!await onAddFriend(selectedUsername)) return;
            setIsModalOpen(false);
            setSelectedUsername('');
        } finally {
            setAdding(false);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setSelectedUsername('');
    };

    const handleSearch = (value: string) => {
        setSelectedUsername(value);
        setOptions(
            allUsers
                .filter(u => u.toLowerCase().includes(value.toLowerCase()))
                .map(u => ({ value: u }))
        );
    };

    return (
        <PageSection
            title="Friends"
            description={`${friends.length} ${friends.length === 1 ? 'friend' : 'friends'}`}
            actions={<Button type="primary" icon={<UserAddOutlined />} onClick={() => setIsModalOpen(true)}>Add Friend</Button>}
        >
            <Surface>
                {friends.length === 0 ? (
                    <EmptyState icon={<TeamOutlined />} title="No friends yet. Add someone!" />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {friends.map(friend => {
                            const initial = friend?.[0]?.toUpperCase() ?? '?';
                            return (
                                <div
                                    key={friend}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '8px 10px',
                                        borderRadius: token.borderRadius,
                                        cursor: 'default',
                                    }}
                                >
                                    <Avatar
                                        size={36}
                                        style={{ background: token.colorPrimaryBg, color: token.colorPrimaryText, fontWeight: 700, flexShrink: 0 }}
                                    >
                                        {initial}
                                    </Avatar>
                                    <Text strong>{friend}</Text>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Surface>

            <Modal
                title="Add a Friend"
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okText="Add"
                confirmLoading={adding}
                okButtonProps={{ disabled: !selectedUsername.trim() }}
            >
                <AutoComplete
                    options={options}
                    style={{ width: '100%' }}
                    onSearch={handleSearch}
                    onSelect={(value: string) => setSelectedUsername(value)}
                    value={selectedUsername}
                >
                    <Input aria-label={text('Benutzername', 'Username')} prefix={<TeamOutlined />} placeholder="Type a username..." />
                </AutoComplete>
            </Modal>
        </PageSection>
    );
};
